import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Breadcrumbs from '../components/Breadcrumbs';
import Footer from '../components/Footer';
import WhatsAppWidget from '../components/WhatsAppWidget';
import { blogPosts } from '../data/blogData';
import { Clock, Calendar, ArrowRight } from 'lucide-react';

const BlogPage = () => {
  const categories = [...new Set(blogPosts.map(post => post.category))];

  return (
    <>
      <Helmet>
        <title>Cleaning Robot Blog | Tips, Guides & Industry Insights | Harpalyk</title>
        <meta name="description" content="Expert articles on robotic pool cleaners, floor robots, window cleaning robots, and lawn mowers. Tips, buying guides, and industry insights from Harpalyk." />
        <link rel="canonical" href="https://harpalyk.com/blog" />
      </Helmet>

      <Header />

      <main className="pt-20">
        <Breadcrumbs items={[{ label: 'Blog' }]} />
        {/* Hero */}
        <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-16 px-6 lg:px-12">
          <div className="max-w-6xl mx-auto text-center">
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">Harpalyk Blog</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Expert insights on robotic cleaning technology. Buying guides, maintenance tips, and industry trends.
            </p>
          </div>
        </section>

        {/* Featured Post */}
        <section className="py-12 px-6 lg:px-12 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Featured Article</h2>
            <Link to={`/blog/${blogPosts[0].slug}`} className="block">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all grid md:grid-cols-2">
                <img 
                  src={blogPosts[0].image} 
                  alt={blogPosts[0].title}
                  className="w-full h-64 md:h-full object-cover"
                />
                <div className="p-8">
                  <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">
                    {blogPosts[0].category}
                  </span>
                  <h3 className="text-2xl font-bold text-gray-900 mt-4 mb-3 hover:text-emerald-600 transition-colors">
                    {blogPosts[0].title}
                  </h3>
                  <p className="text-gray-600 mb-4">{blogPosts[0].excerpt}</p>
                  <div className="flex items-center gap-4 text-gray-500 text-sm">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" /> {blogPosts[0].date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" /> {blogPosts[0].readTime}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>

        {/* All Posts */}
        <section className="py-16 px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">All Articles</h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.slice(1).map((post) => (
                <Link 
                  key={post.id} 
                  to={`/blog/${post.slug}`}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-all group"
                >
                  <img 
                    src={post.image} 
                    alt={post.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-6">
                    <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs font-medium">
                      {post.category}
                    </span>
                    <h3 className="text-lg font-bold text-gray-900 mt-3 mb-2 group-hover:text-emerald-600 transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{post.excerpt}</p>
                    <div className="flex items-center justify-between text-gray-500 text-xs">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {post.readTime}
                      </span>
                      <span className="flex items-center gap-1 text-emerald-600 font-medium group-hover:gap-2 transition-all">
                        Read More <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-6 lg:px-12 bg-emerald-600 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Explore Our Products?</h2>
            <p className="text-emerald-100 mb-8">
              From pool robots to floor cleaners, find the perfect robotic cleaning solution for your needs.
            </p>
            <Link 
              to="/#products" 
              className="inline-block bg-white text-emerald-700 px-8 py-4 rounded-lg font-bold hover:bg-emerald-100 transition-all"
            >
              View All Products
            </Link>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppWidget />
    </>
  );
};

export default BlogPage;
